import { createReducer, on } from '@ngrx/store';
import { ReportTemplate, ReportResult, ScheduleResponse, SCHEDULE_STATUS } from '@models/report-template.model';
import { ReportingActions } from '@store/actions/reporting/reporting.actions';

export const reportingFeatureKey = 'reporting';

export interface State {
  templates: ReportTemplate[];
  isRunning: boolean;
  isViewingTemplate: boolean;
  isExportingAudit: boolean;

  reportResults: ReportResult[];
  reportResultsTotalCount: number;
  isLoadingResults: boolean;
  isViewingResult: boolean;
  isDeletingResult: boolean;

  isBulkRunning: boolean;

  schedules: ScheduleResponse[];
  schedulesTotalCount: number;
  isLoadingSchedules: boolean;
  isSavingSchedule: boolean;
  isDeletingSchedule: boolean;
  scheduleDuplicateError: boolean;
  isReactivatingSchedule: boolean;
  showInactiveSchedules: boolean;

  error: string | null;
}

const REPORT_TEMPLATES: ReportTemplate[] = [
  { id: 'practice-summary', title: 'Practice Summary' },
];

export const initialState: State = {
  templates: REPORT_TEMPLATES,
  isRunning: false,
  isViewingTemplate: false,
  isExportingAudit: false,

  reportResults: [],
  reportResultsTotalCount: 0,
  isLoadingResults: false,
  isViewingResult: false,
  isDeletingResult: false,

  isBulkRunning: false,

  schedules: [],
  schedulesTotalCount: 0,
  isLoadingSchedules: false,
  isSavingSchedule: false,
  isDeletingSchedule: false,
  scheduleDuplicateError: false,
  isReactivatingSchedule: false,
  showInactiveSchedules: false,

  error: null,
};

export const reducer = createReducer(
  initialState,

  // Run Report
  on(ReportingActions.runReport, (state) => ({ ...state, isRunning: true, error: null })),
  on(ReportingActions.runReportSuccess, (state) => ({ ...state, isRunning: false })),
  on(ReportingActions.runReportFailure, (state, { error }) => ({ ...state, isRunning: false, error })),

  on(ReportingActions.bulkRunReport, (state) => ({ ...state, isBulkRunning: true, error: null })),
  on(ReportingActions.bulkRunReportSuccess, (state) => ({ ...state, isBulkRunning: false })),
  on(ReportingActions.bulkRunReportFailure, (state, { error }) => ({ ...state, isBulkRunning: false, error })),
  on(ReportingActions.bulkRunReportPartialFailure, (state, { error }) => ({ ...state, isBulkRunning: false, error })),

  // View Template
  on(ReportingActions.viewReportTemplate, (state) => ({ ...state, isViewingTemplate: true, error: null })),
  on(ReportingActions.viewReportTemplateSuccess, (state) => ({ ...state, isViewingTemplate: false })),
  on(ReportingActions.viewReportTemplateFailure, (state, { error }) => ({ ...state, isViewingTemplate: false, error })),

  // Export Org Audit
  on(ReportingActions.exportOrgAudit, (state) => ({ ...state, isExportingAudit: true, error: null })),
  on(ReportingActions.exportOrgAuditSuccess, (state) => ({ ...state, isExportingAudit: false })),
  on(ReportingActions.exportOrgAuditFailure, (state, { error }) => ({ ...state, isExportingAudit: false, error })),

  // Organizational Reports
  on(ReportingActions.loadReportResults, (state) => ({ ...state, isLoadingResults: true, error: null })),
  on(ReportingActions.loadReportResultsSuccess, (state, { results, totalCount }) => ({
    ...state,
    isLoadingResults: false,
    reportResults: results,
    reportResultsTotalCount: totalCount,
  })),
  on(ReportingActions.loadReportResultsFailure, (state, { error }) => ({
    ...state,
    isLoadingResults: false,
    error,
  })),

  on(ReportingActions.viewReportResult, (state) => ({ ...state, isViewingResult: true, error: null })),
  on(ReportingActions.viewReportResultSuccess, (state) => ({ ...state, isViewingResult: false })),
  on(ReportingActions.viewReportResultFailure, (state, { error }) => ({ ...state, isViewingResult: false, error })),

  on(ReportingActions.deleteReportResult, (state) => ({ ...state, isDeletingResult: true, error: null })),
  on(ReportingActions.deleteReportResultSuccess, (state, { id }) => ({
    ...state,
    isDeletingResult: false,
    reportResults: state.reportResults.filter((result) => result.id !== id),
    reportResultsTotalCount: state.reportResultsTotalCount - 1,
  })),
  on(ReportingActions.deleteReportResultFailure, (state, { error }) => ({
    ...state,
    isDeletingResult: false,
    error,
  })),

  // Scheduled Reports
  on(ReportingActions.loadSchedules, (state) => ({ ...state, isLoadingSchedules: true, error: null })),
  on(ReportingActions.loadSchedulesSuccess, (state, { schedules, totalCount }) => ({
    ...state,
    isLoadingSchedules: false,
    schedules,
    schedulesTotalCount: totalCount,
  })),
  on(ReportingActions.loadSchedulesFailure, (state, { error }) => ({
    ...state,
    isLoadingSchedules: false,
    error,
  })),

  on(ReportingActions.createSchedule, (state) => ({
    ...state,
    isSavingSchedule: true,
    scheduleDuplicateError: false,
    error: null,
  })),
  on(ReportingActions.createScheduleSuccess, (state, { schedules }) => ({
    ...state,
    isSavingSchedule: false,
    schedules: [...state.schedules, ...schedules],
    schedulesTotalCount: state.schedulesTotalCount + schedules.length,
  })),
  on(ReportingActions.createScheduleFailure, (state, { error, isDuplicate }) => ({
    ...state,
    isSavingSchedule: false,
    scheduleDuplicateError: isDuplicate,
    error,
  })),

  on(ReportingActions.updateSchedule, (state) => ({ ...state, isSavingSchedule: true, error: null })),
  on(ReportingActions.updateScheduleSuccess, (state, { schedule }) => ({
    ...state,
    isSavingSchedule: false,
    schedules: state.schedules.map((s) => (s.id === schedule.id ? schedule : s)),
  })),
  on(ReportingActions.updateScheduleFailure, (state, { error }) => ({
    ...state,
    isSavingSchedule: false,
    error,
  })),

  on(ReportingActions.deleteSchedule, (state) => ({ ...state, isDeletingSchedule: true, error: null })),
  on(ReportingActions.deleteScheduleSuccess, (state, { id }) => ({
    ...state,
    isDeletingSchedule: false,
    schedules: state.schedules.map((s) =>
      s.id === id ? { ...s, statusId: SCHEDULE_STATUS.INACTIVE } : s
    ),
  })),
  on(ReportingActions.deleteScheduleFailure, (state, { error }) => ({
    ...state,
    isDeletingSchedule: false,
    error,
  })),

  on(ReportingActions.reactivateSchedule, (state) => ({ ...state, isReactivatingSchedule: true, error: null })),
  on(ReportingActions.reactivateScheduleSuccess, (state, { schedule }) => ({
    ...state,
    isReactivatingSchedule: false,
    schedules: state.schedules.map((s) => s.id === schedule.id ? schedule : s),
  })),
  on(ReportingActions.reactivateScheduleFailure, (state, { error }) => ({
    ...state,
    isReactivatingSchedule: false,
    error,
  })),

  on(ReportingActions.setShowInactiveSchedules, (state, { showInactive }) => ({
    ...state,
    showInactiveSchedules: showInactive,
  })),

  on(ReportingActions.resetRunningState, (state) => ({ ...state, isRunning: false }))
);
